import {
    Avatar,
    Button,
    CircularProgress,
    Divider,
    ListItemIcon,
    Menu,
    MenuItem,
    Toolbar,
} from "@mui/material";
import { LanguagesDropdown } from "components/LanguagesDropdown";
import { paperProps, useStyles } from "./AppToolbar.styles";
import { IAppToolbar } from "./AppToolbarInterfaces";
import { useContext, useMemo, useState } from "react";
import Logout from "@mui/icons-material/Logout";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKey from "@mui/icons-material/VpnKey";
import { removeUser, resetLocalStorage, setToken } from "utilities/storageUtilities";
import { logout, superAdminCorporateLogin } from "api/queries/loginAPI";
import ProfileModal from "./components/ProfileModal/ProfileModal.view";
import { useTranslation } from "react-i18next";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import AppMenu from "./components/AppMenu/AppMenu.view";
import TopBarNav from "./components/TopBarNav/TopBarNav.view";
import { ILoginResponse } from "components/Login/LoginInterfaces";
import {
    CORPORATE_BACKOFFICE_PATHS,
    LocalStorageGlobalKeys,
    MADERA_BACKOFFICE_PATHS,
    USERTYPE,
} from "constants/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsModal from "./components/NotificationsModal/NotificationsModal";
import useRolesPermissions from "hooks/useRolesPermissions";
import { useAllowApiKeyManagement } from "hooks/useAllowApiKeyManagement";

const AppToolbar = ({ appDrawerItems, navLabel }: IAppToolbar) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [isOpenProfileModal, setOpenProfileModal] = useState(false);
    const [isOpenNotificationsModal, setOpenNotificationsModal] = useState(false);
    const {
        currentUser,
        isLoadingCurrentUser,
        rcaEnabled,
        divisions,
        millsWithNoDivision,
        refreshCorporationsAndDivisions,
        refreshCurrentUser,
    } = useContext(CorporateContext);
    const { millId } = useParams();
    const { pathname } = useLocation();

    // Active product module shown in the top bar: Birch = RCA/AP (routes under /rca),
    // Aspen = Downtime (everything else in the corporate backoffice).
    const moduleName = pathname.includes(`/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`) ? "Birch" : "Aspen";
    const [anchorElForUserMenu, setAnchorElForUserMenu] = useState<null | HTMLElement>(null);
    const isOpenUserMenu = Boolean(anchorElForUserMenu);

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const { t } = useTranslation();

    const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        refreshCorporationsAndDivisions();
        refreshCurrentUser();
        setAnchorElForUserMenu(event.currentTarget);
    };
    const handleUserMenuClose = () => {
        setAnchorElForUserMenu(null);
    };

    const handleSuperAdminReturn = async () => {
        let loginResponse = await superAdminCorporateLogin({});

        const { data } = loginResponse as ILoginResponse;

        if (data.token) {
            setToken(data.token);
            navigate(`/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.ROOT}`);
        }
    };

    const handleLogout = () => {
        removeUser();
        logout();
        handleUserMenuClose();
        resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);
        window.location.reload();
    };

    const handleClickProfile = () => {
        setOpenProfileModal(true);
        handleUserMenuClose();
    };

    const handleClickNotification = () => {
        setOpenNotificationsModal(true);
        handleUserMenuClose();
    };

    // Settings + Help were drawer links; the Aspen-Birch refresh moves them into
    // the user menu (above Logout). The API Key Management link (added on dev as a
    // drawer item) gets the same treatment.
    const handleClickSettings = () => {
        navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.SETUP_DASHBOARD}`);
        handleUserMenuClose();
    };

    const handleClickApiKeys = () => {
        navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.API_KEY_MANAGEMENT}`);
        handleUserMenuClose();
    };

    const handleClickHelp = () => {
        navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.DOWNTIME_MODULE_HELP}`);
        handleUserMenuClose();
    };

    // Settings, Help, and API Keys are Aspen-centric (they used to live in the
    // Aspen-only drawer). Now that the user menu is shared across modules, keep
    // them scoped to the Aspen module so they don't leak onto Birch/RCA pages
    // (review feedback on the top-bar refactor).
    const isAspenModule = moduleName === "Aspen";
    const showSettings = isAspenModule && CORPORATE_PERMISSIONS?.sidebarPermissions?.showSettingsLink;
    const showHelp = isAspenModule && process.env.REACT_APP_SHOW_DOWNTIME_HELP?.toLocaleLowerCase() === "true";
    const { isAllowedToUseApiKeyManagement } = useAllowApiKeyManagement();
    const showApiKeys = isAspenModule && isAllowedToUseApiKeyManagement;
    const navDropdownLabel =
        navLabel ?? t("corporate_backoffice.corporate_layout.app_drawer.locations_menu.title");

    // Name of the currently-open site (mill), resolved from the route param against
    // the locations tree, so it can render next to the brand logo.
    const siteName = useMemo(() => {
        const id = Number(millId);
        if (!id) return undefined;
        const direct = millsWithNoDivision?.find((mill) => mill.mill_id === id);
        if (direct) return direct.name;
        for (const division of divisions ?? []) {
            for (const region of division.regions ?? []) {
                const match = region.mills?.find((mill) => mill.mill_id === id);
                if (match) return match.name;
            }
        }
        return undefined;
    }, [millId, divisions, millsWithNoDivision]);

    return (
        <Toolbar>
            <div className={classes.appBar}>
                <div className={classes.appBarLeftContent}>
                    <img src="/images/maderaLogoTrimmed.png" alt="Madera" className={classes.brandLogo} />
                    <Divider orientation="vertical" flexItem className={classes.divider} />
                    <span className={classes.moduleName}>{moduleName}</span>
                    {siteName && (
                        <>
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                            <span className={classes.siteName}>{siteName}</span>
                        </>
                    )}
                </div>
                <div className={classes.appBarRightContent}>
                    {!!appDrawerItems && (
                        <>
                            <TopBarNav label={navDropdownLabel}>{appDrawerItems}</TopBarNav>
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                        </>
                    )}
                    <LanguagesDropdown className={classes.appBarLanguageDropdown} />
                    <Divider orientation="vertical" flexItem className={classes.divider} />
                    <Button
                        id="basic-button"
                        aria-controls={isOpenUserMenu ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={isOpenUserMenu ? "true" : undefined}
                        onClick={handleUserMenuClick}
                        className={classes.menuButton}
                        disableRipple
                    >
                        {isLoadingCurrentUser && <CircularProgress />}
                        {!isLoadingCurrentUser && currentUser && (
                            <>
                                <Avatar className={classes.avatar} src={currentUser.media?.media_url} />
                                {`${currentUser.first_name} ${currentUser.last_name}`}
                                <ExpandMoreIcon
                                    className={isOpenUserMenu ? classes.expandMoreIconRotated : classes.expandMoreIcon}
                                />
                            </>
                        )}
                    </Button>
                    <Menu
                        anchorEl={anchorElForUserMenu}
                        id="basic-menu"
                        open={isOpenUserMenu}
                        onClose={handleUserMenuClose}
                        onClick={handleUserMenuClose}
                        PaperProps={paperProps}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                        <MenuItem onClick={handleClickProfile}>
                            <ListItemIcon>
                                <Avatar className={classes.avatar} src={currentUser?.media?.media_url} />
                            </ListItemIcon>
                            {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile")}
                        </MenuItem>
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.showNotifications && (
                            <MenuItem onClick={handleClickNotification}>
                                <ListItemIcon>
                                    <NotificationsNoneIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notifications")}
                            </MenuItem>
                        )}
                        <Divider />
                        {showApiKeys && (
                            <MenuItem onClick={handleClickApiKeys}>
                                <ListItemIcon>
                                    <VpnKey fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.api_key_management")}
                            </MenuItem>
                        )}
                        {showSettings && (
                            <MenuItem onClick={handleClickSettings}>
                                <ListItemIcon>
                                    <SettingsIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.settings")}
                            </MenuItem>
                        )}
                        {showHelp && (
                            <MenuItem onClick={handleClickHelp}>
                                <ListItemIcon>
                                    <InfoOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.help")}
                            </MenuItem>
                        )}
                        {currentUser?.user_type?.user_type_id === USERTYPE.OSORNOADMINISTRATOR && (
                            <MenuItem onClick={handleSuperAdminReturn}>
                                <ListItemIcon>
                                    <KeyboardReturnIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.madera_bo")}
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                            {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.logout")}
                        </MenuItem>
                    </Menu>
                    {rcaEnabled && (
                        <>
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                            <AppMenu />
                        </>
                    )}
                </div>
            </div>
            {isOpenProfileModal && (
                <ProfileModal open={isOpenProfileModal} user={currentUser} onClose={() => setOpenProfileModal(false)} />
            )}
            {isOpenNotificationsModal && (
                <NotificationsModal open={isOpenNotificationsModal} onClose={() => setOpenNotificationsModal(false)} />
            )}
        </Toolbar>
    );
};

export default AppToolbar;
